import styled, { css } from 'styled-components';

import { BREAKPOINTS } from '@shared/styled-system';

import { BUTTON_COLOR, BUTTON_SIZE, BUTTON_VARIANT, ICON_POSITION } from './types';

export type ButtonElementProps = {
	$color: Lowercase<keyof typeof BUTTON_COLOR>;
	$size?: Lowercase<keyof typeof BUTTON_SIZE>;
	$mobileSize?: Lowercase<keyof typeof BUTTON_SIZE>;
	$variant?: Lowercase<keyof typeof BUTTON_VARIANT>;
	$iconPosition?: Lowercase<keyof typeof ICON_POSITION>;
	$fluid?: boolean;
	$hasText?: boolean;
	$hasIcon?: boolean;
	$minWidth?: number;
};

export const IconWrapper = styled.i`
	display: flex;
	align-items: center;
	justify-content: center;
	margin-right: 0;
	width: 20px;
	height: 20px;

	& svg {
		width: 100%;
		height: auto;
		padding-top: 2px;
	}
`;

export const TextWrapper = styled.div`
	display: flex;
	align-items: center;
	font-weight: 500;
	flex-shrink: 0;

	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

/**
 * With this additional wrapper over the text content of the button, we can override the
 * specified font size (specified below and dependent on the button's `$size` attribute)
 */
export const ButtonStyledText = styled.div<{ $size?: ButtonElementProps['$size'] }>`
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;

	& a {
		color: ${({ theme }) => theme.colors.text.title};
	}

	${({ $size }) => {
		switch ($size) {
			case BUTTON_SIZE.EXTRA_SMALL:
				return css`
					font-size: 13px;
				`;
			case BUTTON_SIZE.SMALL:
				return css`
					font-size: 13px;
				`;
			case BUTTON_SIZE.MEDIUM:
				return css`
					font-size: 14px;
				`;
			case BUTTON_SIZE.LARGE:
				return css`
					font-size: 16px;
				`;
			default:
				return css``;
		}
	}}
`;

const sizeMixin = (size: Lowercase<keyof typeof BUTTON_SIZE>) => {
	if (size === BUTTON_SIZE.EXTRA_SMALL) {
		return css`
			padding: 6px;
			min-height: 30px;

			${TextWrapper} {
				font-size: 13px;
				font-weight: 500;
				line-height: 18px;
			}

			${IconWrapper} {
				& svg {
					width: 16px;
				}
			}
		`;
	}
	if (size === BUTTON_SIZE.SMALL) {
		return css`
			padding: 8px;
			min-height: 34px;

			${TextWrapper} {
				font-size: 13px;
				font-weight: 500;
				line-height: 18px;
			}

			${IconWrapper} {
				& svg {
					width: 16px;
				}
			}
		`;
	}
	if (size === BUTTON_SIZE.MEDIUM) {
		return css`
			padding: 8px;
			min-height: 36px;

			${TextWrapper} {
				font-size: 14px;
				font-weight: 500;
				line-height: 22px;
			}

			${IconWrapper} {
				& svg {
					width: 20px;
				}
			}
		`;
	}
	if (size === BUTTON_SIZE.LARGE) {
		return css`
			padding: 8px;
			min-height: 42px;

			${TextWrapper} {
				font-size: 16px;
				font-weight: 500;
				line-height: 26px;
			}

			${IconWrapper} {
				& svg {
					width: 24px;
				}
			}
		`;
	}
	return css``;
};

export const ButtonElement = styled.button<ButtonElementProps>`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	border-radius: 4px;
	border: none;
	cursor: pointer;
	position: relative;
	font-weight: 500;
	min-width: ${({ $minWidth }) => ($minWidth ? $minWidth + 'px' : 'inherit')};
	transition: all 0.2s;

	&:disabled {
		cursor: not-allowed;
	}

	& svg {
		vertical-align: middle;
	}

	background-color: ${({ theme, $color }) => theme.components.buttons[$color]?.normal.backgroundColor};
	color: ${({ theme, $color }) => theme.components.buttons[$color]?.normal.text};

	& svg path:last-of-type,
	& svg path.glyph,
	& svg circle {
		fill: ${({ theme, $color }) => theme.components.buttons[$color]?.normal.icon};
	}

	&:hover,
	&:focus {
		background-color: ${({ theme, $color }) => theme.components.buttons[$color]?.hover.backgroundColor};
		color: ${({ theme, $color }) => theme.components.buttons[$color]?.hover.text};

		& svg path:last-of-type,
		& svg path.glyph,
		& svg circle {
			fill: ${({ theme, $color }) => theme.components.buttons[$color]?.hover.icon};
		}
	}

	&:active {
		background-color: ${({ theme, $color }) => theme.components.buttons[$color]?.active.backgroundColor};
		color: ${({ theme, $color }) => theme.components.buttons[$color]?.active.text};

		& svg path:last-of-type,
		& svg path.glyph,
		& svg circle {
			fill: ${({ theme, $color }) => theme.components.buttons[$color]?.active.icon};
		}
	}

	&:disabled {
		background-color: ${({ theme, $color }) => theme.components.buttons[$color]?.disabled.backgroundColor};
		color: ${({ theme, $color }) => theme.components.buttons[$color]?.disabled.text};

		& svg path:last-of-type,
		& svg path.glyph,
		& svg circle {
			fill: ${({ theme, $color }) => theme.components.buttons[$color]?.disabled.icon};
		}
	}

	${props => props.$size && sizeMixin(props.$size)}

	@media (${BREAKPOINTS.MOBILE}) {
		${props => props.$mobileSize && sizeMixin(props.$mobileSize)}
	}

	${props =>
		props.$fluid &&
		css`
			width: 100%;
		`}

	${props =>
		props.$hasIcon &&
		props.$hasText &&
		css`
			padding-left: 8px;

			${IconWrapper} {
				margin-right: 4px;
				margin-left: 0;
			}
		`}

  ${props =>
		props.$hasIcon &&
		!props.$hasText &&
		css`
			padding: 0 4px;
		`}

  ${props =>
		props.$variant === BUTTON_VARIANT.ROUND &&
		css`
			border-radius: 12px;
			padding: 8px;
			min-height: 40px;
			min-width: 40px;

			& svg {
				padding-top: 0;
			}
		`}

  ${props =>
		props.$variant === BUTTON_VARIANT.ROUNDED &&
		css`
			border-radius: 8px;
			padding: 4px;
			min-height: 32px;
			min-width: 32px;

			${IconWrapper} {
				width: 24px;
				height: 24px;
				& svg {
					padding-top: 0;
					width: 24px;
				}
			}
		`}
`;
